import {
  calcMaxDepositPercentage,
  calcMinDepositPercentage,
} from '@/lib/payment/v12';

import type { V12Product } from './type';

export function getDurationValue(
  loanProducts: V12Product[],
  value?: V12Product,
) {
  let v = 0;
  for (let i = 0; i < loanProducts.length; i++) {
    if (loanProducts[i].months === value?.months) {
      v = i;
      break;
    }
  }
  return v;
}

export function validateDeposit(amount: number, min: number, max: number) {
  return (
    !!calcMaxDepositPercentage(amount, min) &&
    !!calcMinDepositPercentage(amount, max)
  );
}

const defaultMarkParts = [4, 5]; // Try dividing the range into these numbers of segments (in priority order)
// Calculate the appropriate step (integer) prioritize finding a divisor that splits the range evenly,
export function computeStep(min: number, max: number): number {
  const span = max - min;
  if (span <= 0) return 1;

  // Calculate the minimum allowed spacing (10% of the entire range)  const minSpacing = span * 0.1;
  const minSpacing = span * 0.1;

  for (const parts of defaultMarkParts) {
    const candidateStep = span / parts;
    // Check if the step size is large enough and can divide the range
    if (candidateStep >= minSpacing && span % candidateStep === 0) {
      return candidateStep;
    }
  }

  // Fallback: Try to find a large enough step size
  // Start with a maximum of 5 segments, then gradually reduce the number of segments until you find a suitable step size
  for (let parts = 5; parts >= 2; parts--) {
    const candidateStep = span / parts;
    if (candidateStep >= minSpacing) {
      return Math.ceil(candidateStep);
    }
  }

  // If all attempts fail, use the minimum distance as the step size
  return Math.max(1, Math.ceil(minSpacing));
}

// Create percentage markers to ensure reasonable distribution of marker points
export function createPercentageMarks(min: number, max: number) {
  const span = max - min;

  // if the range is very small, return the minimum and maximum values directly
  if (span <= 1) {
    const marks = [min, max].filter(
      (value, index, array) => array.indexOf(value) === index,
    );
    const markLabels = marks.reduce<Record<number, string>>((acc, pct) => {
      acc[pct] = `${pct}%`;
      return acc;
    }, {});
    return { marks, markLabels };
  }

  const step = computeStep(min, max);

  const marks: number[] = [min];

  // Add the middle marker
  let current = min;
  while (current < max) {
    current = Math.min(current + step, max);
    const roundedCurrent = Math.round(current);

    // Avoid adding points that are too close to the previous marked point
    if (
      roundedCurrent > marks[marks.length - 1] &&
      roundedCurrent - marks[marks.length - 1] >= span * 0.1
    ) {
      marks.push(roundedCurrent);
    }
  }

  // Make sure the last marked point is max and has a reasonable distance from the previous point
  if (marks[marks.length - 1] !== max) {
    if (max - marks[marks.length - 1] < span * 0.1) {
      // If it is too close to max, remove the previous point
      marks.pop();
    }
    // Make sure not to add duplicate points
    if (marks[marks.length - 1] !== max) {
      marks.push(max);
    }
  }

  const uniqueMarks = marks.filter(
    (value, index, array) => array.indexOf(value) === index,
  );

  const markLabels = uniqueMarks.reduce<Record<number, string>>((acc, pct) => {
    acc[pct] = `${pct}%`;
    return acc;
  }, {});

  return { marks: uniqueMarks, markLabels };
}
